// ================================================
// CLARITY REPORTING — SETTINGS PAGE
// Includes report delivery schedule controls
// ================================================

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Sidebar from '../components/common/Sidebar';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h   = i === 0 ? 12 : i > 12 ? i - 12 : i;
  const ampm = i < 12 ? 'AM' : 'PM';
  return { value: `${String(i).padStart(2,'0')}:00`, label: `${h}:00 ${ampm}` };
});

const TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (ET)' },
  { value: 'America/Chicago',     label: 'Central (CT)' },
  { value: 'America/Denver',      label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix',     label: 'Arizona (AZ)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (HT)' },
  { value: 'Europe/London',       label: 'London (GMT)' },
  { value: 'Europe/Paris',        label: 'Paris (CET)' },
  { value: 'Australia/Sydney',    label: 'Sydney (AEDT)' },
  { value: 'Asia/Tokyo',          label: 'Tokyo (JST)' },
];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [gscSites, setGscSites] = useState([]);

  // Schedule form state
  const [reportDay,      setReportDay]      = useState('monday');
  const [reportTime,     setReportTime]     = useState('08:00');
  const [reportTimezone, setReportTimezone] = useState('America/New_York');
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    fetchSettings();

    // Handle GSC callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('gsc') === 'connected') {
      toast.success('Google Search Console connected successfully!');
      const sites = params.get('sites');
      if (sites) setGscSites(JSON.parse(decodeURIComponent(sites)));
      window.history.replaceState({}, '', '/settings');
    } else if (params.get('gsc') === 'error') {
      toast.error('Failed to connect Google Search Console. Please try again.');
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  async function fetchSettings() {
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
      setReportDay(data.report_day || 'monday');
      setReportTime(data.report_time?.slice(0,5) || '08:00');
      setReportTimezone(data.report_timezone || 'America/New_York');
      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveSchedule(e) {
    e.preventDefault();
    setSavingSchedule(true);
    try {
      await api.put('/settings/report-schedule', {
        report_day: reportDay,
        report_time: reportTime,
        report_timezone: reportTimezone
      });
      toast.success('Report schedule updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put('/settings/profile', { first_name: firstName, last_name: lastName });
      updateUser({ first_name: firstName, last_name: lastName });
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleConnectGSC() {
    try {
      const { data } = await api.get('/auth/google');
      window.location.href = data.url;
    } catch {
      toast.error('Failed to initiate Google connection');
    }
  }

  async function handleDisconnectGSC() {
    try {
      await api.delete('/auth/google');
      updateUser({ gsc_connected: false });
      setSettings(s => ({ ...s, gsc_connected: false }));
      toast.success('Google Search Console disconnected');
    } catch {
      toast.error('Failed to disconnect');
    }
  }

  async function handleSelectGscProperty(propertyUrl) {
    try {
      await api.put('/settings/gsc-property', { property_url: propertyUrl });
      toast.success('GSC property selected');
    } catch {
      toast.error('Failed to save property selection');
    }
  }

  if (loading) return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="loading">Loading settings&hellip;</div>
      </main>
    </div>
  );

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">
        <div className="page-header">
          <h1>Settings</h1>
        </div>

        <div className="page-container" style={{ maxWidth: 720 }}>

          {/* --- Profile --- */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Profile</span>
            </div>
            <form onSubmit={handleSaveProfile}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">First Name</label>
                  <input
                    className="form-input"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Last Name</label>
                  <input
                    className="form-input"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-group mb-20">
                <label className="form-label">Email Address</label>
                <input className="form-input" value={settings?.email || ''} disabled
                  style={{ background: 'var(--color-gray-100)', color: 'var(--color-gray-500)' }}
                />
                <div className="form-hint">Contact support to change your email address.</div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>

          {/* --- Report Delivery Schedule --- */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Report Delivery Schedule</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 24 }}>
              Choose when your weekly Clarity Report arrives in your inbox.
            </p>
            <form onSubmit={handleSaveSchedule}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Day</label>
                  <select
                    className="form-select"
                    value={reportDay}
                    onChange={e => setReportDay(e.target.value)}
                  >
                    {DAYS.map(d => (
                      <option key={d} value={d}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Time</label>
                  <select
                    className="form-select"
                    value={reportTime}
                    onChange={e => setReportTime(e.target.value)}
                  >
                    {HOURS.map(h => (
                      <option key={h.value} value={h.value}>{h.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Timezone</label>
                  <select
                    className="form-select"
                    value={reportTimezone}
                    onChange={e => setReportTimezone(e.target.value)}
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginBottom: 20 }}>
                Your next report will arrive on <strong>{reportDay.charAt(0).toUpperCase() + reportDay.slice(1)}</strong> at <strong>{HOURS.find(h => h.value === reportTime)?.label}</strong> ({TIMEZONES.find(tz => tz.value === reportTimezone)?.label})
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingSchedule}>
                {savingSchedule ? 'Saving...' : 'Save Schedule'}
              </button>
            </form>
          </div>

          {/* --- Google Search Console --- */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Google Search Console</span>
              {settings?.gsc_connected && (
                <span style={{
                  fontSize: 11, background: 'var(--color-success-bg)',
                  color: 'var(--color-success-text)', padding: '3px 10px',
                  fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase'
                }}>
                  Connected
                </span>
              )}
            </div>

            {settings?.gsc_connected ? (
              <>
                <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 16 }}>
                  Your Google Search Console is connected. Your reports will use direct Google data
                  for more accurate keyword rankings, impressions, and click-through rates.
                </p>
                {gscSites.length > 0 && (
                  <div className="mb-20">
                    <label className="form-label">Select Your Property</label>
                    <select
                      className="form-select"
                      defaultValue={settings?.gsc_property_url || ''}
                      onChange={e => handleSelectGscProperty(e.target.value)}
                    >
                      <option value="">Select a property...</option>
                      {gscSites.map(site => (
                        <option key={site} value={site}>{site}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button className="btn btn-danger btn-sm" onClick={handleDisconnectGSC}>
                  Disconnect Google Search Console
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: 'var(--color-gray-500)', marginBottom: 20 }}>
                  Connect your Google Search Console to unlock richer, more accurate data directly
                  from Google — including impressions, click-through rates, and exact position data.
                </p>
                <button className="btn btn-primary" onClick={handleConnectGSC}>
                  Connect Google Search Console
                </button>
              </>
            )}
          </div>

          {/* --- Subscription --- */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Subscription</span>
            </div>
            <div style={{ fontSize: 13, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: 'var(--border-light)' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>Current Plan</span>
                <span style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontSize: 12 }}>
                  {user?.account_type?.replace('_', ' ')}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm">Upgrade Plan</button>
              <button className="btn btn-danger btn-sm">Cancel Subscription</button>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
